/**
 * AVM2 frame lifecycle passes (duck-typed over display objects to avoid import cycles).
 *
 * Per tick (FlashPlayer.tick), following Flash Player / Ruffle (`run_all_phases_avm2`):
 *   1. Enter: playing timelines advance (children before parents, reverse render order): removals,
 *      frame number change, PlaceObject updates, new children instantiated (not yet constructed).
 *   2. ENTER_FRAME broadcast.
 *   3. Construct: new timeline objects are constructed (post-order): named children are bound as
 *      properties of their parent, ADDED/ADDED_TO_STAGE fire. FRAME_CONSTRUCTED broadcast.
 *   4. Frame scripts: pending frame scripts run in display-list order (parent before children).
 *   5. EXIT_FRAME broadcast.
 * An explicit goto runs steps 3-5 immediately ("inner goto frame", SWF >= 10 semantics).
 */
import { rt } from './runtime';
import { Event } from '../events/Event';

/** Path of child indices from the root; off-stage objects sort after on-stage ones. */
function orderKey(o: any): { onStage: boolean; path: number[] } {
  const path: number[] = [];
  let cur = o;
  while (cur.$parent) {
    const p = cur.$parent;
    const kids: any[] | null = p.$lifecycleChildren();
    path.push(kids ? kids.indexOf(cur) : 0);
    cur = p;
  }
  path.reverse();
  return { onStage: !!cur.$isStage, path };
}

export function sortByTreeOrder<T>(objs: T[]): T[] {
  if (objs.length <= 1) return objs;
  const keyed = objs.map((o, i) => ({ o, i, k: orderKey(o) }));
  keyed.sort((a, b) => {
    if (a.k.onStage !== b.k.onStage) return a.k.onStage ? -1 : 1;
    if (!a.k.onStage) return a.i - b.i;
    const pa = a.k.path;
    const pb = b.k.path;
    const n = Math.min(pa.length, pb.length);
    for (let j = 0; j < n; j++) if (pa[j] !== pb[j]) return pa[j] - pb[j];
    return pa.length - pb.length; // ancestors first
  });
  return keyed.map((x) => x.o);
}

function bindName(parent: any, name: string, child: any): void {
  try {
    parent[name] = child;
  } catch (err) {
    // Flash reports "Error setting AVM2 child named ..." and continues.
    rt().errors.push(err);
  }
}

/** Construct `o` and its unconstructed descendants (children first). */
export function constructObject(o: any): void {
  if (o.$constructed) return;
  const kids: any[] | null = o.$lifecycleChildren();
  if (kids) {
    for (const k of kids.slice()) if (!k.$constructed) constructObject(k);
  }
  o.$constructed = true;
  rt().unconstructed.delete(o);
  const parent = o.$parent;
  if (parent && !o.$placedByScript) {
    if (o.$explicitName && o.$name) bindName(parent, o.$name, o);
    if (!parent.$isButton) {
      o.$dispatch(new Event(Event.ADDED, true, false));
      if (o.stage) o.$dispatch(new Event(Event.ADDED_TO_STAGE, false, false));
    }
  }
}

export function constructPass(): void {
  const r = rt();
  let guard = 0;
  while (r.unconstructed.size && guard++ < 64) {
    const all = [...r.unconstructed];
    const roots = all.filter((o: any) => !o.$parent || o.$parent.$constructed);
    const list = roots.length ? sortByTreeOrder(roots) : all;
    for (const o of list) constructObject(o);
  }
}

export function frameScriptsPass(): void {
  const r = rt();
  let guard = 0;
  while (r.pendingScripts.size && guard++ < 1000) {
    const list = sortByTreeOrder([...r.pendingScripts]);
    for (const clip of list as any[]) {
      if (!r.pendingScripts.has(clip)) continue;
      r.pendingScripts.delete(clip);
      clip.$runLocalFrameScript();
    }
  }
  if (r.fresh.length) {
    for (const c of r.fresh) c.$fresh = false;
    r.fresh = [];
  }
}

function isDisplayObject(o: any): boolean {
  return typeof o.$renderKind === 'string';
}

export function broadcastFrameEvent(type: 'enterFrame' | 'frameConstructed' | 'exitFrame' | 'render'): void {
  rt().broadcastEvent(type, () => new Event(type, false, false), isDisplayObject);
}

/** Construct + FRAME_CONSTRUCTED + frame scripts + EXIT_FRAME, used by explicit gotos. */
export function runInnerGotoFrame(): void {
  const r = rt();
  const old = r.phase;
  r.phase = 'construct';
  constructPass();
  broadcastFrameEvent('frameConstructed');
  r.phase = 'scripts';
  frameScriptsPass();
  r.phase = 'exit';
  broadcastFrameEvent('exitFrame');
  r.phase = old;
}
